import {
  AlertTriangleIcon,
  ArchiveIcon,
  AwardIcon,
  BellIcon,
  BookmarkIcon,
  BookOpenIcon,
  BoxesIcon,
  BrainIcon,
  BriefcaseIcon,
  BuildingIcon,
  CalendarIcon,
  CameraIcon,
  CheckCircleIcon,
  CircleDotIcon,
  ClipboardListIcon,
  ClockIcon,
  CloudIcon,
  CodeIcon,
  CompassIcon,
  CpuIcon,
  DatabaseIcon,
  FileSearch2Icon,
  FileTextIcon,
  FilterIcon,
  FlagIcon,
  FlameIcon,
  GiftIcon,
  GitCompareIcon,
  GlobeIcon,
  HeadphonesIcon,
  HeartIcon,
  HomeIcon,
  ImageIcon,
  KeyIcon,
  KeyRoundIcon,
  LayersIcon,
  LayoutListIcon,
  LeafIcon,
  LightbulbIcon,
  LinkIcon,
  ListChecksIcon,
  LockIcon,
  MailIcon,
  MapIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  MicIcon,
  MonitorIcon,
  MoonIcon,
  MusicIcon,
  NavigationIcon,
  PenToolIcon,
  PlayCircleIcon,
  PlusCircleIcon,
  ScaleIcon,
  SearchIcon,
  ServerIcon,
  SettingsIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  ShieldIcon,
  SlidersIcon,
  SparklesIcon,
  StarIcon,
  SunIcon,
  TagIcon,
  TargetIcon,
  TerminalIcon,
  ThumbsUpIcon,
  TrendingUpIcon,
  TrophyIcon,
  UnplugIcon,
  UserCheckIcon,
  UserIcon,
  UsersIcon,
  VideoIcon,
  WrenchIcon,
  ZapIcon,
  type LucideIcon,
} from 'lucide-react';

/**
 * Lucide-icon-name → component map for persisted quick actions.
 *
 * Keys MUST match the strings stored in `user_preferences.quick_actions[*].icon`
 * (see server seed defaults in user-prefs.service.ts). Unknown keys fall
 * back to <SparklesIcon /> so the UI never blanks out on a typo.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  // AI / Analysis
  Sparkles: SparklesIcon,
  Brain: BrainIcon,
  Lightbulb: LightbulbIcon,
  Cpu: CpuIcon,
  TrendingUp: TrendingUpIcon,

  // Documents / Text
  FileText: FileTextIcon,
  FileSearch2: FileSearch2Icon,
  BookOpen: BookOpenIcon,
  ClipboardList: ClipboardListIcon,
  ListChecks: ListChecksIcon,
  Archive: ArchiveIcon,
  Boxes: BoxesIcon,

  // Code / Tech
  Code: CodeIcon,
  Terminal: TerminalIcon,
  GitCompare: GitCompareIcon,
  Database: DatabaseIcon,
  Server: ServerIcon,
  Monitor: MonitorIcon,
  Layers: LayersIcon,

  // Communication
  Mail: MailIcon,
  MessageCircle: MessageCircleIcon,
  MessageSquare: MessageSquareIcon,
  Bell: BellIcon,
  Mic: MicIcon,
  Headphones: HeadphonesIcon,
  Video: VideoIcon,
  Music: MusicIcon,

  // Security / Compliance
  Shield: ShieldIcon,
  ShieldAlert: ShieldAlertIcon,
  ShieldCheck: ShieldCheckIcon,
  Lock: LockIcon,
  Key: KeyIcon,
  KeyRound: KeyRoundIcon,
  Scale: ScaleIcon,
  AlertTriangle: AlertTriangleIcon,

  // People / Business
  User: UserIcon,
  Users: UsersIcon,
  UserCheck: UserCheckIcon,
  Briefcase: BriefcaseIcon,
  Building: BuildingIcon,
  Award: AwardIcon,
  Trophy: TrophyIcon,

  // Navigation / World
  Globe: GlobeIcon,
  Home: HomeIcon,
  Map: MapIcon,
  Compass: CompassIcon,
  Navigation: NavigationIcon,
  Flag: FlagIcon,

  // UI / Actions
  Search: SearchIcon,
  Filter: FilterIcon,
  Settings: SettingsIcon,
  Sliders: SlidersIcon,
  Tag: TagIcon,
  Bookmark: BookmarkIcon,
  Star: StarIcon,
  Heart: HeartIcon,
  ThumbsUp: ThumbsUpIcon,
  CheckCircle: CheckCircleIcon,
  CircleDot: CircleDotIcon,
  PlusCircle: PlusCircleIcon,

  // Productivity
  Calendar: CalendarIcon,
  Clock: ClockIcon,
  Target: TargetIcon,
  LayoutList: LayoutListIcon,
  PenTool: PenToolIcon,

  // Tools
  Wrench: WrenchIcon,
  Zap: ZapIcon,
  Flame: FlameIcon,
  PlayCircle: PlayCircleIcon,
  Link: LinkIcon,
  Unplug: UnplugIcon,
  Gift: GiftIcon,

  // Nature / Misc
  Leaf: LeafIcon,
  Sun: SunIcon,
  Moon: MoonIcon,
  Cloud: CloudIcon,
  Camera: CameraIcon,
  Image: ImageIcon,
};

export function iconFromKey(key: string): LucideIcon {
  return ICON_MAP[key] ?? SparklesIcon;
}

export const ICON_KEYS = Object.keys(ICON_MAP);

/** All icon keys offered in the customize Sheet picker (same as ICON_MAP keys, in display order). */
export const ICON_PICKER_KEYS: ReadonlyArray<string> = ICON_KEYS;

/** Curated color palette offered in the customize Sheet — mirrors PROJECT_COLORS. */
export const QUICK_ACTION_COLORS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: '#60a5fa', label: 'Blue' },
  { value: '#38bdf8', label: 'Sky' },
  { value: '#a78bfa', label: 'Violet' },
  { value: '#f472b6', label: 'Pink' },
  { value: '#fb923c', label: 'Orange' },
  { value: '#fbbf24', label: 'Amber' },
  { value: '#34d399', label: 'Emerald' },
  { value: '#94a3b8', label: 'Slate' },
];
